/**
 * Base class for each of the object and element in the scene
 */

// @ts-ignore
import PubSub from 'pubsub-js';
import { Box3, ColorRepresentation, Object3D, Vector2, Vector3 } from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

import { DynamicAssetType } from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../services/web-api';
import { isDefined } from '../../utils/variables';
import { CoordinateManager } from '../coordinate-manager';
import { GltfLoader } from '../loader/gltf';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { Point2d } from '../utilities/math-helpers';

export enum AssetSaveState {
  Idle,
  Saving,
  Error,
}

export type AssetCategory = 'area' | 'line' | 'design' | 'model' | 'terrain-model' | 'cad' | 'poi';
export type AssetType =
  | 'area'
  | 'dxf'
  | 'line'
  | 'design'
  | 'model'
  | 'terrain-model'
  | 'glb'
  | 'poi';

export type AssetLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type LabelSettings = {
  showHeightLabels: boolean;
  showLengthLabels: boolean;
  color: ColorRepresentation;
  fontSize: number;
  sizeAttenuation: boolean;
  decimalPlaces: number;
};

export abstract class ElementObj extends Object3D {
  abstract get assetType(): AssetType;

  /**
   * Returns a value indicating whether the asset is automatically saved upon change.
   */
  get autoSave(): boolean {
    return this._autoSave;
  }

  /**
   * Specifies whether the asset should be automatically saved upon change.
   */
  set autoSave(value: boolean) {
    this._autoSave = value;
  }

  timelineContainerId?: string | null;

  // The id of the saved element
  assetId?: string;
  labelsGroup?: Object3D;
  labelsSettings?: LabelSettings;

  saveState: AssetSaveState = AssetSaveState.Idle;

  downloadable = false;
  objType?: DynamicAssetType;

  get freezeState() {
    return this.freeze;
  }

  // translate, rotate or scale.
  freeze = {
    translate: true,
    rotateZ: true,
    rotateXY: true,
    scale: true,
  };

  // This defined is it is possible to delete the object from the viewer
  // Currently models are not to be deleted
  protected deletable = true;

  wgs84?: { lng: number; lat: number };
  MAS?: number;

  // Visiblity settings
  private stateVisibility = true;
  private categoryVisibility = true;
  private filterVisibility = true;
  private cadFolderVisibility = true;

  loadingStatus: AssetLoadingStatus = this.initialLoadingStatus();

  private _autoSave = true;

  selected = false;

  protected _assetTagIds?: string[];
  get assetTagIds() {
    return this._assetTagIds ?? [];
  }
  set assetTagIds(value: string[]) {
    this._assetTagIds = value;
  }

  constructor(timelineContainerId: string | null) {
    super();

    this.timelineContainerId = timelineContainerId;
  }

  /**
   * Returns the initial loading status. Some assets might need to be loaded before data is fully
   * visible in the asset menu. Other assets might not.
   */
  abstract initialLoadingStatus(): AssetLoadingStatus;

  setPosition(newPosition: Vector3) {
    this.position.copy(newPosition);
  }

  patch(updatedPoi: ElementObj): void {
    /** */
  }

  isSelected() {
    return this.selected;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
  }

  async onChange() {
    // Whenever the asset is changed, it is automatically saved
    if (this.autoSave) {
      await this.save();
    }

    publish(SubscriptionTopic.AssetChanged, this);
  }

  /**
   * @description This invokes the asset manager to save this asset
   */
  async save() {
    /**
     * Note: Parent is AssetManager, but importing asset manager from this file creates
     * a circular dependency. So this is a work-around to avoid that.
     * Ideally this should be refactored; for example remove save function from the assets
     * and only call save from asset manager.
     */
    await (this.parent as any)?.save?.(this);
  }

  /**
   * Loads the skymap information model data related to the asset using signed request.
   */
  async loadSkyMapInformationModel(fileVersionId?: string, sharedViewId?: string): Promise<GLTF> {
    const {
      data: { url },
    } = sharedViewId
      ? await SkyMapAxiosServiceFactory.instance.createSharedViewServiceV1().getAssetGeometryUrl({
          path: {
            sharedViewId,
            dynamicAssetId: this.assetId!,
          },
          query: {
            fileVersionId,
          },
        })
      : await SkyMapAxiosServiceFactory.instance.createAssetServiceV1().getAssetGeometryUrl({
          path: {
            dynamicAssetId: this.assetId!,
          },
          query: {
            fileVersionId,
          },
        });

    const arrayBuffer = await WebApi.fetchS3DataAsArrayBuffer(url);
    return await GltfLoader.instance.loadGltf(Buffer.from(arrayBuffer));
  }

  /**
   * @description This is called every time the object is transfomed by the transform tool.
   * Override to do anything upon changes
   */
  onTransformed() {
    return;
  }

  /**
   * @description Perfoms actions when it is selected
   */
  onSelect() {
    this.selected = true;
    PubSub.publish('assetSelected', { uuid: this.uuid });
  }

  /**
   * @description Performs actions when it is unselected
   */
  onDeselect() {
    this.selected = false;
  }

  //////////////////////////////////////////////////////
  ///
  /// Visibility functionality
  ///

  getStateVisibility() {
    // @ts-ignore
    return this.visible;
  }

  private applyCompositeVisibility() {
    this.visible =
      this.stateVisibility &&
      this.categoryVisibility &&
      this.filterVisibility &&
      this.cadFolderVisibility;
  }

  async setStateVisibility(state: boolean) {
    if (this.stateVisibility === state) {
      return;
    }
    this.stateVisibility = state;
    this.applyCompositeVisibility();
    await this.publishOnStateVisibilityChangedEvent();
  }

  async setCategoryVisibility(state: boolean) {
    if (this.categoryVisibility === state) {
      return;
    }
    this.categoryVisibility = state;
    this.applyCompositeVisibility();
    await this.publishOnStateVisibilityChangedEvent();
  }

  async setFilterVisibility(state: boolean) {
    if (this.filterVisibility === state) {
      return;
    }
    this.filterVisibility = state;
    this.applyCompositeVisibility();
    await this.publishOnStateVisibilityChangedEvent();
  }

  async setCadFolderVisibility(state: boolean) {
    if (this.cadFolderVisibility === state) {
      return;
    }
    this.cadFolderVisibility = state;
    this.applyCompositeVisibility();
    await this.publishOnStateVisibilityChangedEvent();
  }

  async publishOnStateVisibilityChangedEvent() {
    publish(SubscriptionTopic.SkyviewAssetVisibilityChanged, {
      assetUuid: this.uuid,
      visible: this.visible,
    });

    await this.onStateVisibilityChanged();
  }

  /**
   *  @description Called when state visibility is changed. Can be overridden bu sub-classes
   */
  onStateVisibilityChanged(): Promise<void> {
    return Promise.resolve();
  }

  async toggleVisibility(visible?: boolean) {
    this.stateVisibility = visible === undefined ? !this.stateVisibility : visible;
    this.applyCompositeVisibility();
    await this.publishOnStateVisibilityChangedEvent();
  }

  /**
   * @param toWorldCoordinates
   * @description Generate the elemnt objects bounding box
   * @returns {Box3}
   */
  generateBoundingBox(toWorldCoordinates = false) {
    if (toWorldCoordinates) {
      return new Box3().setFromObject(this);
    }

    const bb: Box3[] = [];

    /**
     * @param child
     */
    function fetchBoundingBox(child: any) {
      if (child.geometry) {
        child.geometry.computeBoundingBox();
        bb.push(child.geometry.boundingBox as Box3);
      }

      for (const nestedChild of child.children) {
        fetchBoundingBox(nestedChild);
      }
    }

    fetchBoundingBox(this);

    const bbMin = new Vector3(
      Number.MAX_SAFE_INTEGER,
      Number.MAX_SAFE_INTEGER,
      Number.MAX_SAFE_INTEGER,
    );
    const bbMax = new Vector3(
      Number.MIN_SAFE_INTEGER,
      Number.MIN_SAFE_INTEGER,
      Number.MIN_SAFE_INTEGER,
    );
    for (const box of bb) {
      if (box.min.x < bbMin.x) {
        bbMin.x = box.min.x;
      }
      if (box.min.y < bbMin.y) {
        bbMin.y = box.min.y;
      }
      if (box.min.z < bbMin.z) {
        bbMin.z = box.min.z;
      }
      if (box.max.x > bbMax.x) {
        bbMax.x = box.max.x;
      }
      if (box.max.y > bbMax.y) {
        bbMax.y = box.max.y;
      }
      if (box.max.z > bbMax.z) {
        bbMax.z = box.max.z;
      }
    }

    return new Box3(bbMin, bbMax);
  }

  /**
   * @param localPos
   * @description Convert the elements position to wgs84 coordinates
   */
  calculateWGS84Coords(localPos: Vector2) {
    const projection = CoordinateManager.getInstance().getProjectProjection();
    const projectionorigo = CoordinateManager.getInstance().getProjectionOrigo();

    if (!projection || !projectionorigo) {
      return undefined;
    }

    const xCoord = localPos.x + (projectionorigo.x || 0);
    const yCoord = localPos.y + (projectionorigo.y || 0);

    return CoordinateManager.getInstance().convertCoords(xCoord, yCoord, projection, 'wgs84');
  }

  /**
   * @description Calculates the elements position to wgs84 coordinates
   */
  calculateLocalPosition(): Point2d | undefined {
    if (!isDefined(this.wgs84?.lng) || !isDefined(this.wgs84?.lat)) {
      return undefined;
    }

    const projection = CoordinateManager.getInstance().getProjectProjection();
    const projectionorigo = CoordinateManager.getInstance().getProjectionOrigo();

    if (!projection || !projectionorigo) {
      return undefined;
    }

    const projectionPos = CoordinateManager.getInstance().convertCoords(
      this.wgs84.lng,
      this.wgs84.lat,
      'wgs84',
      projection,
    );
    return {
      x: projectionPos.x - (projectionorigo.x || 0),
      y: projectionPos.y - (projectionorigo.y || 0),
    };
  }

  /**
   * @description Check if MAS information available
   * @returns {object}
   */
  availableMAS() {
    return ProjectGeodataManager.instance().isMasAvailable();
  }

  /**
   * @param height
   * @description Calculates the elements MAS  based on the assets local height
   * @returns {object}
   */
  calculateMAS(height = this.position.z || 0) {
    return (
      (ProjectGeodataManager.instance().isMasAvailable()
        ? CoordinateManager.getInstance().getMAS()
        : this.MAS || 0) + height
    );
  }

  protected getMasBase(): number {
    return CoordinateManager.getInstance().getMAS() ?? 0;
  }

  /**
   *
   *
   * @param MAS
   * @description Calculates the elements local height based on the assets MAS
   * @returns {object}
   */
  calculateLocalHeight(MAS = this.MAS || 0) {
    const projectMAS = CoordinateManager.getInstance().getMAS();
    if (!isDefined(projectMAS)) {
      return undefined;
    }

    return MAS - projectMAS;
  }

  /**
   *  @description Return the data used by a native application
   */
  getNativeAssetData() {
    throw new Error(`getNativeAssetData is not implemented ${this.constructor.name}`);
  }

  equals(elementObject: ElementObj) {
    return elementObject.assetId === this.assetId;
  }

  /**
   * Determines if the asset can be displayed.
   *
   * For example for assets connected to the file manager some file versions
   * may be processed and valid while other can be in running or failed state.
   */
  canBeDisplayed() {
    return true;
  }
}
